import { Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';

import {
  DatePair,
  extractDateOnly,
  isDateRangeOverMaxMonths,
} from '../../filterHelpers';
import DateRangeInput from './DateRangeInput';
import DateRangeMonthlyInput from './DateRangeMonthlyInput';
import DateShortcutSelector from './DateShortcutSelector';

interface DateFilterGroupProps {
  datePairs: DatePair[];
  onDateChange: (
    value: string,
    type: 'start' | 'end',
    dateKey?: string,
  ) => void;
  showShortcuts?: boolean;
  maxMonths?: number;
  shortcutDirection?: 'past' | 'future';
}

const DateFilterGroup: React.FC<DateFilterGroupProps> = ({
  datePairs,
  onDateChange,
  showShortcuts = true,
  maxMonths,
  shortcutDirection = 'past',
}) => {
  const [useShortcuts, setUseShortcuts] = useState(false);
  const [shortcuts, setShortcuts] = useState<{ [pairIndex: number]: string }>(
    {},
  );
  const [validationError, setValidationError] = useState<string | null>(null);

  const dateRangeSignature = datePairs
    .map(
      (p) =>
        `${String(p.startFilter.value ?? '')}|${String(p.endFilter.value ?? '')}`,
    )
    .join(';');

  useEffect(() => {
    if (maxMonths === undefined) {
      setValidationError(null);
      return;
    }
    for (const pair of datePairs) {
      const startValue = String(pair.startFilter.value ?? '');
      const endValue = String(pair.endFilter.value ?? '');
      if (
        startValue &&
        endValue &&
        isDateRangeOverMaxMonths(startValue, endValue, maxMonths)
      ) {
        setValidationError(
          `La diferencia entre las fechas no puede ser mayor a ${maxMonths} meses`,
        );
        return;
      }
    }
    setValidationError(null);
  }, [dateRangeSignature, maxMonths, datePairs]);

  const handleShortcutChange = (
    pairIndex: number,
    pair: DatePair,
    shortcut: string,
    startDate?: string,
    endDate?: string,
  ) => {
    setShortcuts((prev) => ({ ...prev, [pairIndex]: shortcut }));

    if (shortcut === 'none') {
      if (pair.startFilter.dateKey && pair.endFilter.dateKey) {
        onDateChange('', 'start', pair.startFilter.dateKey);
        onDateChange('', 'end', pair.endFilter.dateKey);
      }
    } else if (startDate || endDate) {
      if (startDate && !endDate && pair.startFilter.dateKey) {
        onDateChange(startDate, 'start', pair.startFilter.dateKey);
        if (pair.endFilter.dateKey) {
          onDateChange('', 'end', pair.endFilter.dateKey);
        }
      } else if (endDate && !startDate && pair.endFilter.dateKey) {
        onDateChange(endDate, 'end', pair.endFilter.dateKey);
        if (pair.startFilter.dateKey) {
          onDateChange('', 'start', pair.startFilter.dateKey);
        }
      } else if (startDate && endDate) {
        if (pair.startFilter.dateKey && pair.endFilter.dateKey) {
          onDateChange(startDate, 'start', pair.startFilter.dateKey);
          onDateChange(endDate, 'end', pair.endFilter.dateKey);
        }
      }
    }
  };

  const handleDateChange = (
    value: string,
    type: 'start' | 'end',
    dateKey: string,
  ) => {
    onDateChange(value, type, dateKey);
  };

  if (datePairs.length === 0) return null;

  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="body1" fontWeight="bold" marginBottom={1}>
          Fechas
        </Typography>
        {showShortcuts && (
          <Typography
            variant="body1"
            fontWeight="bold"
            onClick={() => setUseShortcuts(!useShortcuts)}
            style={{ cursor: 'pointer', color: 'blue' }}
          >
            {useShortcuts ? 'Ocultar' : 'Mostrar'} atajos
          </Typography>
        )}
      </div>
      {validationError && (
        <Typography
          variant="caption"
          color="error"
          display="block"
          marginBottom={1}
        >
          {validationError}
        </Typography>
      )}

      {useShortcuts ? (
        <div>
          {datePairs.map((pair, index) =>
            pair.startFilter.type === 'monthly' ? (
              <></>
            ) : (
              <DateShortcutSelector
                key={index}
                label={pair.label}
                dateType={pair.startFilter.dateType || 'request'}
                selectedShortcut={shortcuts[index] || 'none'}
                onShortcutChange={(shortcut, startDate, endDate) =>
                  handleShortcutChange(
                    index,
                    pair,
                    shortcut,
                    startDate,
                    endDate,
                  )
                }
                shortcutDirection={shortcutDirection}
              />
            ),
          )}
        </div>
      ) : (
        <div>
          {datePairs.map((pair, index) =>
            pair.startFilter.type === 'monthly' ? (
              <DateRangeMonthlyInput
                key={index}
                label={pair.label}
                startValue={extractDateOnly(
                  (pair.startFilter.value as string) || '',
                )}
                endValue={extractDateOnly(
                  (pair.endFilter.value as string) || '',
                )}
                onStartChange={(value) =>
                  handleDateChange(
                    value,
                    'start',
                    pair.startFilter.dateKey || '',
                  )
                }
                onEndChange={(value) =>
                  handleDateChange(value, 'end', pair.endFilter.dateKey || '')
                }
              />
            ) : (
              <DateRangeInput
                key={index}
                label={pair.label}
                startValue={extractDateOnly(
                  (pair.startFilter.value as string) || '',
                )}
                endValue={extractDateOnly(
                  (pair.endFilter.value as string) || '',
                )}
                onStartChange={(value) =>
                  handleDateChange(
                    value,
                    'start' as const,
                    pair.startFilter.dateKey || '',
                  )
                }
                onEndChange={(value) =>
                  handleDateChange(
                    value,
                    'end' as const,
                    pair.endFilter.dateKey || '',
                  )
                }
                maxMonths={maxMonths}
              />
            ),
          )}
        </div>
      )}
    </div>
  );
};

export default DateFilterGroup;
